import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import { QUERY_KEY } from '../../constants'

import { abrViewContentsList } from '../../utils/abr'

import { StoresContext } from '../../provider/StoreProvider'

import { getArticles, getArticleCategories } from '../../api/article'

import { Head } from '../../components/head/Head'
import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { CenterWrap } from '../../components/common'
import { Header } from '../../components/header'
import { InfinityScroll } from '../../components/pagingList/InfinityScroll'
import { Category } from '../../components/paramOption/Category'
import { Spinner } from '../../components/spinner/Spinner'
import { ContentList, ContentItem } from './components'

const HEAD_INFO = {
  title: '콘텐츠 보기 | 미술품 조각투자 플랫폼, 아티피오',
  description: '아티피오가 준비한 다양한 콘텐츠를 확인해 보세요.',
  ogTitle: '콘텐츠 보기 | 미술품 조각투자 플랫폼, 아티피오',
  ogDescription: '아티피오가 준비한 다양한 콘텐츠를 확인해 보세요.',
  ogUrl: window.location.href,
}

export function Contents() {
  const {
    commonStore: { setActiveTab, setShowTab },
  } = useContext(StoresContext)

  const [categoryId, setCategoryId] = useState<number | undefined>()

  // 콘텐츠 카테고리
  const {
    data: categories,
    isPending: categoriesIsPending,
    isError: categoriesIsError,
  } = useQuery({ queryKey: [QUERY_KEY.articleCategories], queryFn: getArticleCategories })
  // 콘텐츠 목록
  const { data, isPending, isError, hasNextPage, refetch, fetchNextPage } = useInfiniteQuery({
    queryKey: [QUERY_KEY.articles, { categoryId }],
    queryFn: async ({ pageParam = 0 }) =>
      await getArticles({
        offset: pageParam,
        size: 50,
        categoryId: typeof categoryId === 'number' ? categoryId : undefined,
      }),
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.offset + lastPage.size),
    initialPageParam: 0,
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => page.list).flat(),
    }),
  })

  useEffect(() => {
    setActiveTab('contents')
    setShowTab(true)
    getArticleCategories()
    abrViewContentsList()
  }, [])

  if (isPending || isError || categoriesIsPending || categoriesIsError) {
    return (
      <>
        <Head {...HEAD_INFO} />
        <Wrap>
          <StyledContent>
            <CenterWrap>
              <Spinner />
            </CenterWrap>
          </StyledContent>
        </Wrap>
      </>
    )
  }

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header type="SUB" title="콘텐츠" isBack={false} />
        <StyledContent id="infinity-scroll-target" isPullEvent={true} onPull={refetch}>
          <Category categoryList={categories.list} categoryId={categoryId} setCategoryId={setCategoryId} />
          <InfinityScroll length={data.pages.length} isLast={!hasNextPage} next={fetchNextPage}>
            <ContentList>
              {data.pages.map(({ id, title, imageUri }) => (
                <ContentItem key={id} title={title} imgUri={imageUri} href={`/contents/${id}`} line={2} />
              ))}
            </ContentList>
          </InfinityScroll>
        </StyledContent>
      </Wrap>
    </>
  )
}

const StyledContent = styled(Content)`
  height: 100%;
  padding-top: 16px;
`
